import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { NoticeDto, NoticeService } from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import PaginationComponent from '../../../components/pagination/pagination.component';
import { NotFoundComponent } from '../../../components/not-found/not-found.component';
import { SeoService } from '../../../services/seo.service';

@Component({
  selector: 'app-notice',
  imports: [
    CommonModule,
    Icon,
    RouterLink,
    PaginationComponent,
    NotFoundComponent,
  ],
  templateUrl: './notice.page.html',
})
export default class NoticePage {
  private readonly noticeService = inject<NoticeService>(NoticeService);
  private readonly seoService = inject(SeoService);

  query = signal<string>('');
  pageNo = signal<number>(1);
  totalItemsPerPage = 8;

  private searchSubject = new Subject<string>();

  constructor() {
    this.seoService.set({
      title: '공지사항',
      description:
        '밀리언쇼의 최신 공지사항, 안내사항, 방문 정보를 확인해보세요.',
      path: '/customer/notice',
    });

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(searchQuery => {
        this.query.set(searchQuery);
        this.pageNo.set(1);
      });
  }

  $pinnedData = rxResource({
    stream: ({ params }) => {
      return this.noticeService.noticeControllerSearchV1({
        pageNo: 1,
        pageSize: 9999,
        isExposed: true,
        isPinned: true,
        query: params.query,
        type: params.type,
      });
    },
    params: () => ({
      query: this.query(),
      type: 'MILLIONSHOW' as NoticeDto['type'],
    }),
  });

  regularPageSize = computed(() => {
    const pinnedCount = this.pinnedNotices().length;
    return Math.max(1, this.totalItemsPerPage - pinnedCount);
  });

  $regularData = rxResource({
    stream: ({ params }) => {
      return this.noticeService.noticeControllerSearchV1({
        pageNo: params.pageNo,
        pageSize: params.pageSize,
        isExposed: true,
        isPinned: false,
        query: params.query,
        type: params.type,
      });
    },
    params: () => ({
      pageNo: this.pageNo(),
      pageSize: this.regularPageSize(),
      query: this.query(),
      type: 'MILLIONSHOW' as NoticeDto['type'],
    }),
  });

  pinnedNotices = computed(() => this.$pinnedData.value()?.items || []);
  regularNotices = computed(() => this.$regularData.value()?.items || []);

  totalCount = computed(() => {
    const pinnedCount = this.$pinnedData.value()?.pageInfo?.totalItems || 0;
    const regularCount = this.$regularData.value()?.pageInfo?.totalItems || 0;
    return pinnedCount + regularCount;
  });

  totalPages = computed<number[]>(() => {
    const totalItems = this.$regularData.value()?.pageInfo?.totalItems || 0;
    const count = Math.max(1, Math.ceil(totalItems / this.regularPageSize()));
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  currentPage = computed(() => this.pageNo());

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onPageChange(page: number): void {
    this.pageNo.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  }
}
